/**
 * TASK-260: Extraction Transform Utilities
 *
 * Shared helper functions for transforming backend RAG extraction
 * output to frontend ExtractionResult format.
 *
 * Backend returns camelCase fields with {value, confidence} wrappers.
 * Frontend uses snake_case fields with the same wrapper structure.
 */

/**
 * Unwrap {value, confidence} structure from backend
 * Returns the raw value or undefined if not present
 */
export const unwrapField = (field: unknown): unknown => {
  if (field === undefined || field === null) return undefined;
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return (field as { value: unknown }).value;
  }
  return field;
};

/**
 * Get confidence from backend {value, confidence} wrapper
 * Returns field.confidence if present, otherwise returns default
 */
export const getConfidence = (field: unknown, defaultConf: number): number => {
  if (
    field &&
    typeof field === 'object' &&
    'confidence' in field &&
    typeof (field as { confidence: number }).confidence === 'number'
  ) {
    return (field as { confidence: number }).confidence;
  }
  return defaultConf;
};

/**
 * Wrap value with confidence for ExtractionResult format
 * Unwraps the field first, validates it's not empty, then re-wraps
 */
export const wrapField = <T>(
  field: unknown,
  defaultConf = 0.85
): { value: T; confidence: number } | undefined => {
  const value = unwrapField(field);
  if (value === undefined || value === null || value === '') return undefined;
  const confidence = getConfidence(field, defaultConf);
  return { value: value as T, confidence };
};

/**
 * Transform an array of backend items, applying a transformer to each
 */
export const transformArray = <TInput, TOutput>(
  arr: TInput[] | undefined | null,
  itemTransformer: (item: TInput, baseConfidence: number) => TOutput,
  baseConfidence: number
): TOutput[] | undefined => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return undefined;
  return arr.map((item) => itemTransformer(item, baseConfidence));
};

/**
 * Safely get the first element of an array field
 * Handles both wrapped and unwrapped arrays
 */
export const getFirstArrayElement = <T>(field: unknown): T | undefined => {
  const arr = unwrapField(field);
  if (Array.isArray(arr) && arr.length > 0) {
    return arr[0] as T;
  }
  return undefined;
};

/**
 * Transform a wealth source item from backend format
 */
export const transformWealthSource = (
  item: Record<string, unknown>,
  baseConfidence: number
): Record<string, { value: unknown; confidence: number } | undefined> => {
  const conf = baseConfidence * 0.85;
  return {
    source_type: wrapField(item.wealthSourceType || item.sourceType, conf),
    source_description: wrapField(item.wealthSourceDescription || item.sourceDescription, conf),
    percentage: wrapField(item.wealthSourcePercentage || item.percentage, conf),
    documented: wrapField(item.wealthSourceDocumented || item.documented, conf),
    acquisition_period: wrapField(item.acquisitionPeriod, conf),
    acquisition_start_date: wrapField(item.wealthSourceAcquisitionStartDate || item.acquisitionStartDate, conf),
    acquisition_end_date: wrapField(item.wealthSourceAcquisitionEndDate || item.acquisitionEndDate, conf),
    is_ongoing: wrapField(item.wealthSourceIsOngoing || item.isOngoing, conf),
    years_accumulated: wrapField(item.wealthSourceYearsAccumulated || item.yearsAccumulated, conf),
    asset_type: wrapField(item.wealthSourceAssetType || item.assetType, conf),
    asset_value: wrapField(item.wealthSourceAssetValue || item.assetValue, conf),
    asset_currency: wrapField(item.wealthSourceAssetCurrency || item.assetCurrency, conf),
    asset_location: wrapField(item.wealthSourceAssetLocation || item.assetLocation, conf),
    documentation_type: wrapField(item.wealthSourceDocumentationType || item.documentationType, conf),
    verification_notes: wrapField(item.wealthSourceVerificationNotes || item.verificationNotes, conf),
    liquidity_level: wrapField(item.wealthSourceLiquidity || item.liquidityLevel, conf),
    liquidation_timeframe: wrapField(item.wealthSourceLiquidationTimeframe || item.liquidationTimeframe, conf),
    risk_level: wrapField(item.wealthSourceRiskLevel || item.riskLevel, conf),
    requires_enhanced_verification: wrapField(
      item.wealthSourceRequiresEnhancedVerification || item.requiresEnhancedVerification,
      conf
    ),
    supporting_document_types: wrapField(
      item.wealthSourceSupportingDocumentTypes || item.supportingDocumentTypes,
      conf
    ),
  };
};

/**
 * Transform an income source item from backend format
 */
export const transformIncomeSource = (
  item: Record<string, unknown>,
  baseConfidence: number
): Record<string, { value: unknown; confidence: number } | undefined> => {
  const conf = baseConfidence * 0.85;
  return {
    income_type: wrapField(item.incomeType, conf),
    income_description: wrapField(item.incomeDescription, conf),
    annual_amount: wrapField(item.incomeAnnualAmount || item.annualAmount, conf),
    currency: wrapField(item.incomeCurrency || item.currency, conf),
    frequency: wrapField(item.incomeFrequency || item.frequency, conf),
    source_name: wrapField(item.incomeSourceName || item.sourceName, conf),
    source_country: wrapField(item.incomeSourceCountry || item.sourceCountry, conf),
    start_date: wrapField(item.incomeStartDate || item.startDate, conf),
    is_ongoing: wrapField(item.incomeIsOngoing || item.isOngoing, conf),
    verified: wrapField(item.incomeVerified || item.verified, conf),
    tax_paid: wrapField(item.incomeTaxPaid || item.taxPaid, conf),
    tax_jurisdiction: wrapField(item.incomeTaxJurisdiction || item.taxJurisdiction, conf),
    tax_rate: wrapField(item.incomeTaxRate || item.taxRate, conf),
    stability_rating: wrapField(item.incomeStabilityRating || item.stabilityRating, conf),
    expected_continuity: wrapField(item.incomeExpectedContinuity || item.expectedContinuity, conf),
    supporting_documents: wrapField(item.incomeSupportingDocuments || item.supportingDocuments, conf),
  };
};

/**
 * Transform a business activity item from backend format
 */
export const transformBusinessActivity = (
  item: Record<string, unknown>,
  baseConfidence: number
): Record<string, { value: unknown; confidence: number } | undefined> => {
  const conf = baseConfidence * 0.85;
  return {
    business_name: wrapField(item.businessName, conf),
    business_type: wrapField(item.businessType, conf),
    registration_number: wrapField(item.registrationNumber, conf),
    registration_country: wrapField(item.registrationCountry, conf),
    primary_industry: wrapField(item.primaryIndustry, conf),
    secondary_industries: wrapField(item.secondaryIndustries, conf),
    industry_risk_level: wrapField(item.industryRiskLevel, conf),
    ownership_percentage: wrapField(item.ownershipPercentage, conf),
    ownership_type: wrapField(item.ownershipType, conf),
    other_owners: wrapField(item.otherOwners, conf),
    countries_of_operation: wrapField(item.countriesOfOperation, conf),
    high_risk_countries: wrapField(item.highRiskCountries, conf),
    years_in_business: wrapField(item.yearsInBusiness, conf),
    number_of_employees: wrapField(item.numberOfEmployees, conf),
    annual_revenue: wrapField(item.annualRevenue, conf),
    revenue_currency: wrapField(item.revenueCurrency, conf),
    cash_intensive: wrapField(item.cashIntensive, conf),
    cross_border_transactions: wrapField(item.crossBorderTransactions, conf),
    complex_ownership: wrapField(item.complexOwnership, conf),
  };
};

// ============================================================================
// TASK-261: Profile Extraction Transformation
// ============================================================================

import type { ExtractionResult, ExtractedClientData } from '../services/compliance/types';

/**
 * TASK-261: Build address field for FormDataMapper
 * Returns FieldWithConfidence<ExtractedAddress> structure
 * Checks residentialAddress nested object first, then flat contactInfo fields
 */
const buildAddressField = (
  residentialAddress: Record<string, unknown>,
  contactInfo: Record<string, unknown>,
  get: (camelKey: string, snakeKey: string) => unknown,
  baseConfidence: number
): { value: { street?: string; city?: string; postal_code?: string; country?: string; state_province?: string }; confidence: number } | undefined => {
  const street = (residentialAddress.street ?? residentialAddress.streetAddress ?? contactInfo.streetAddress ?? contactInfo.street ?? get('address', 'street_address')) as string | undefined;
  const city = (residentialAddress.city ?? contactInfo.city ?? get('city', 'city')) as string | undefined;
  const postalCode = (residentialAddress.postal_code ?? residentialAddress.postalCode ?? contactInfo.postalCode ?? contactInfo.postal_code ?? get('postalCode', 'postal_code')) as string | undefined;
  const country = (residentialAddress.country ?? contactInfo.country ?? get('country', 'country')) as string | undefined;
  const state = (residentialAddress.state ?? residentialAddress.canton ?? contactInfo.state ?? contactInfo.canton ?? get('state', 'canton')) as string | undefined;

  // Only return if we have at least some address data
  if (!street && !city && !postalCode && !country && !state) {
    return undefined;
  }

  return {
    value: {
      street: street || undefined,
      city: city || undefined,
      postal_code: postalCode || undefined,
      country: country || undefined,
      state_province: state || undefined,
    },
    confidence: baseConfidence,
  };
};

/**
 * Count non-empty fields in an object recursively
 */
const countFields = (obj: unknown): number => {
  if (!obj || typeof obj !== 'object') return 0;
  let count = 0;
  for (const [, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object' && !Array.isArray(value)) {
        count += countFields(value);
      } else {
        count += 1;
      }
    }
  }
  return count;
};

/**
 * Transform backend /extract-profile response to ExtractionResult format
 * TASK-261: Used by DocumentUploadStep for FormDataMapper integration
 *
 * Backend response format:
 * {
 *   kyc_data: { ... },           // Nested KYC structure
 *   profile_data: { ... },       // Flattened profile
 *   confidence: number,
 *   overall_confidence: number,
 *   fields_extracted: number,
 *   warnings: string[]
 * }
 */
export function transformProfileExtractionToResult(
  response: Record<string, unknown>,
  uploadId: string
): ExtractionResult {
  // Get KYC data from response (try multiple possible locations)
  const kycData = (response.kyc_data || response.extracted_data || response) as Record<string, unknown>;
  const profileData = response.profile_data as Record<string, unknown> | undefined;

  // Calculate confidence
  const confidence = (
    typeof response.confidence === 'number' ? response.confidence :
    typeof response.overall_confidence === 'number' ? response.overall_confidence :
    0.8
  );

  // Calculate fields extracted
  const fieldsExtracted = (
    typeof response.fields_extracted === 'number' ? response.fields_extracted :
    countFields(kycData) + (profileData ? countFields(profileData) : 0)
  );

  // Map backend KYC data to ExtractedClientData format
  const clientData = mapBackendToClientData(kycData, profileData, confidence);

  return {
    success: true,
    upload_id: uploadId,
    extraction_timestamp: new Date().toISOString(),
    confidence,
    fields_extracted: fieldsExtracted,
    fields_total: 100,
    client_data: clientData,
    warnings: Array.isArray(response.warnings)
      ? response.warnings.map((w: string) => ({ field: 'general', message: w }))
      : undefined,
  };
}

/**
 * Map backend KYC/profile data to ExtractedClientData structure
 * Handles both camelCase (frontend normalized) and snake_case (raw backend) formats
 */
function mapBackendToClientData(
  kycData: Record<string, unknown>,
  profileData: Record<string, unknown> | undefined,
  baseConfidence: number
): ExtractedClientData {
  // Helper to get from either kycData or profileData with camelCase/snake_case fallbacks
  const get = (camelKey: string, snakeKey: string): unknown => {
    return kycData[camelKey] ?? kycData[snakeKey] ?? profileData?.[camelKey] ?? profileData?.[snakeKey];
  };

  // Get nested sections (backend may have them nested or flat)
  const personalInfo = (get('personalInfo', 'personal_info') || {}) as Record<string, unknown>;
  const contactInfo = (get('contactInfo', 'contact_info') || {}) as Record<string, unknown>;
  const employmentInfo = (get('employmentInfo', 'employment_info') || {}) as Record<string, unknown>;
  const taxInfo = (get('taxInfo', 'tax_info') || {}) as Record<string, unknown>;
  const pepStatus = (get('pepStatus', 'pep_status') || {}) as Record<string, unknown>;
  const wealthSources = get('wealthSources', 'wealth_sources') as unknown[] | undefined;
  const incomeSources = get('incomeSources', 'income_sources') as unknown[] | undefined;
  const businessActivities = get('businessActivities', 'business_activities') as unknown[] | undefined;
  const financialInfo = (get('financialInfo', 'financial_info') || {}) as Record<string, unknown>;
  const personalBackground = (get('personalBackground', 'personal_background') || {}) as Record<string, unknown>;
  const wealthSummary = (get('wealthSummary', 'wealth_summary') || {}) as Record<string, unknown>;
  const identification = (get('identification', 'identification') || {}) as Record<string, unknown>;

  // TASK-261: Extract nested address objects
  // Backend may return address as: { value: { street, city, ... }, confidence: 0.87 }
  // Or as direct object: { street, city, ... }
  const unwrapAddress = (addr: unknown): Record<string, unknown> => {
    if (!addr || typeof addr !== 'object') return {};
    const addrObj = addr as Record<string, unknown>;
    // Check if it's wrapped in {value, confidence} format
    if ('value' in addrObj && typeof addrObj.value === 'object' && addrObj.value !== null) {
      return addrObj.value as Record<string, unknown>;
    }
    return addrObj;
  };

  const residentialAddress = unwrapAddress(contactInfo.residentialAddress ?? contactInfo.residential_address);
  const mailingAddress = unwrapAddress(contactInfo.mailingAddress ?? contactInfo.mailing_address);
  const workAddress = unwrapAddress(employmentInfo.workAddress ?? employmentInfo.work_address ?? employmentInfo.employerAddress);

  // TASK-261: Also check profile_data sections for additional data
  const section1 = (profileData?.section1 ?? {}) as Record<string, unknown>;
  const section3 = (profileData?.section3 ?? {}) as Record<string, unknown>;

  // Also check section1 for address (profile_data.section1.residentialAddress)
  const section1Address = unwrapAddress(section1.residentialAddress ?? section1.residential_address);
  // Merge addresses: prefer contactInfo, fallback to section1
  const mergedResidentialAddress = Object.keys(residentialAddress).length > 0 ? residentialAddress : section1Address;

  return {
    personal_info: {
      first_name: wrapField(personalInfo.firstName ?? personalInfo.first_name ?? section1.firstName ?? get('firstName', 'first_name'), baseConfidence),
      last_name: wrapField(personalInfo.lastName ?? personalInfo.last_name ?? section1.lastName ?? get('lastName', 'last_name'), baseConfidence),
      middle_name: wrapField(personalInfo.middleName ?? personalInfo.middle_name ?? section1.middleName ?? get('middleName', 'middle_name'), baseConfidence),
      date_of_birth: wrapField(personalInfo.dateOfBirth ?? personalInfo.date_of_birth ?? section1.dateOfBirth ?? get('dateOfBirth', 'date_of_birth'), baseConfidence),
      place_of_birth: wrapField(personalInfo.placeOfBirth ?? personalInfo.place_of_birth ?? get('placeOfBirth', 'place_of_birth'), baseConfidence),
      country_of_birth: wrapField(personalInfo.countryOfBirth ?? personalInfo.country_of_birth ?? get('countryOfBirth', 'country_of_birth'), baseConfidence),
      nationalities: wrapField(personalInfo.nationalities ?? personalInfo.nationality ?? section1.nationalities ?? get('nationalities', 'nationality'), baseConfidence),
      gender: wrapField(personalInfo.gender ?? get('gender', 'gender'), baseConfidence),
      marital_status: wrapField(personalInfo.maritalStatus ?? personalInfo.marital_status ?? section1.maritalStatus ?? get('maritalStatus', 'marital_status'), baseConfidence),
      // TASK-261: Additional personal info fields
      languages: wrapField(personalInfo.languages ?? get('languages', 'languages'), baseConfidence),
      passport_number: wrapField(personalInfo.passportNumber ?? personalInfo.passport_number ?? identification.passportNumber ?? identification.passport_number, baseConfidence),
      national_id: wrapField(personalInfo.nationalId ?? personalInfo.national_id ?? identification.idCardNumber ?? identification.id_card_number, baseConfidence),
      ssn: wrapField(personalInfo.ssn ?? personalInfo.ahvNumber ?? identification.ahvNumber ?? identification.ahv_number, baseConfidence),
      former_names: wrapField(personalInfo.formerNames ?? personalInfo.former_names, baseConfidence),
    },
    contact_info: {
      phone: wrapField(contactInfo.phonePrimary ?? contactInfo.phone_primary ?? contactInfo.phone ?? section1.phonePrimary ?? get('phone', 'phone_primary'), baseConfidence),
      phone_secondary: wrapField(contactInfo.phoneSecondary ?? contactInfo.phone_secondary, baseConfidence),
      mobile_phone: wrapField(contactInfo.mobilePhone ?? contactInfo.mobile_phone, baseConfidence),
      email: wrapField(contactInfo.emailPrimary ?? contactInfo.email_primary ?? contactInfo.email ?? section1.emailPrimary ?? get('email', 'email_primary'), baseConfidence),
      email_secondary: wrapField(contactInfo.emailSecondary ?? contactInfo.email_secondary, baseConfidence),
      // TASK-261: Address must be inside contact_info for FormDataMapper
      // Backend returns contactInfo.residentialAddress as nested object with {value, confidence}
      address: buildAddressField(mergedResidentialAddress, contactInfo, get, baseConfidence),
      mailing_address: buildAddressField(mailingAddress, {}, () => undefined, baseConfidence),
      preferred_contact_method: wrapField(contactInfo.preferredContactMethod ?? contactInfo.preferred_contact_method, baseConfidence),
      relationship_reason: wrapField(contactInfo.relationshipReason ?? contactInfo.relationship_reason, baseConfidence),
      relationship_purpose: wrapField(contactInfo.relationshipPurpose ?? contactInfo.relationship_purpose, baseConfidence),
    },
    // Keep top-level address for backward compatibility with other mappers
    address: {
      street: wrapField(mergedResidentialAddress.street ?? mergedResidentialAddress.streetAddress ?? contactInfo.streetAddress ?? contactInfo.street ?? get('address', 'street_address'), baseConfidence),
      city: wrapField(mergedResidentialAddress.city ?? contactInfo.city ?? get('city', 'city'), baseConfidence),
      postal_code: wrapField(mergedResidentialAddress.postal_code ?? mergedResidentialAddress.postalCode ?? contactInfo.postalCode ?? contactInfo.postal_code ?? get('postalCode', 'postal_code'), baseConfidence),
      country: wrapField(mergedResidentialAddress.country ?? contactInfo.country ?? get('country', 'country'), baseConfidence),
      state_province: wrapField(mergedResidentialAddress.state ?? mergedResidentialAddress.canton ?? contactInfo.state ?? contactInfo.canton ?? get('state', 'canton'), baseConfidence),
    },
    employment_info: {
      status: wrapField(employmentInfo.employmentStatus ?? employmentInfo.employment_status ?? section1.employmentStatus ?? get('employmentStatus', 'employment_status'), baseConfidence),
      occupation: wrapField(employmentInfo.occupation ?? section1.occupation ?? get('occupation', 'occupation'), baseConfidence),
      employer: wrapField(employmentInfo.employerName ?? employmentInfo.employer_name ?? section1.employerName ?? get('employerName', 'employer_name'), baseConfidence),
      industry: wrapField(employmentInfo.employmentIndustry ?? employmentInfo.employment_industry ?? get('employmentIndustry', 'employment_industry'), baseConfidence),
      years_employed: wrapField(employmentInfo.yearsEmployed ?? employmentInfo.years_employed, baseConfidence),
      annual_income: wrapField(employmentInfo.annualIncome ?? employmentInfo.annual_income, baseConfidence),
      income_currency: wrapField(employmentInfo.incomeCurrency ?? employmentInfo.income_currency, baseConfidence),
      work_address: buildAddressField(workAddress, {}, () => undefined, baseConfidence),
    },
    tax_info: {
      residence: wrapField(taxInfo.taxResidence ?? taxInfo.tax_residence ?? section1.taxResidence ?? get('taxResidence', 'tax_residence'), baseConfidence),
      tax_id: wrapField(taxInfo.taxId ?? taxInfo.tax_id ?? get('taxId', 'tax_id'), baseConfidence),
      us_person: wrapField(taxInfo.usPerson ?? taxInfo.us_person ?? get('usPerson', 'us_person'), baseConfidence),
      fatca_status: wrapField(taxInfo.fatcaStatus ?? taxInfo.fatca_status ?? get('fatcaStatus', 'fatca_status'), baseConfidence),
      tax_jurisdictions: wrapField(taxInfo.taxJurisdictions ?? taxInfo.tax_jurisdictions, baseConfidence),
    },
    pep_info: {
      is_pep: wrapField(pepStatus.isPEP ?? pepStatus.is_pep ?? get('isPEP', 'is_pep'), baseConfidence),
      is_rca: wrapField(pepStatus.isRCA ?? pepStatus.is_rca ?? get('isRCA', 'is_rca'), baseConfidence),
      pep_classification: wrapField(pepStatus.pepClassification ?? pepStatus.pep_classification ?? pepStatus.classification, baseConfidence),
      pep_type: wrapField(pepStatus.pepType ?? pepStatus.pep_type ?? get('pepType', 'pep_type'), baseConfidence),
      pep_role: wrapField(pepStatus.pepRole ?? pepStatus.pep_role, baseConfidence),
      pep_relation: wrapField(pepStatus.pepRelation ?? pepStatus.pep_relation, baseConfidence),
      pep_position: wrapField(pepStatus.pepPosition ?? pepStatus.pep_position ?? get('pepPosition', 'pep_position'), baseConfidence),
      pep_positions: wrapField(pepStatus.pepPositions ?? pepStatus.pep_positions ?? pepStatus.positions, baseConfidence),
      pep_country: wrapField(pepStatus.pepCountry ?? pepStatus.pep_country ?? get('pepCountry', 'pep_country'), baseConfidence),
      pep_countries: wrapField(pepStatus.pepCountries ?? pepStatus.pep_countries ?? pepStatus.countries, baseConfidence),
      pep_start_date: wrapField(pepStatus.pepStartDate ?? pepStatus.pep_start_date ?? pepStatus.startDate, baseConfidence),
      pep_end_date: wrapField(pepStatus.pepEndDate ?? pepStatus.pep_end_date ?? pepStatus.endDate, baseConfidence),
      pep_years_in_position: wrapField(pepStatus.pepYearsInPosition ?? pepStatus.pep_years_in_position ?? pepStatus.yearsInPosition, baseConfidence),
      enhanced_due_diligence: wrapField(pepStatus.enhancedDueDiligence ?? pepStatus.enhanced_due_diligence, baseConfidence),
      rca_relationship: wrapField(pepStatus.rcaRelationship ?? pepStatus.rca_relationship, baseConfidence),
      rca_pep_name: wrapField(pepStatus.rcaPEPName ?? pepStatus.rca_pep_name, baseConfidence),
    },
    financial_info: {
      total_assets: wrapField(financialInfo.totalAssets ?? financialInfo.total_assets ?? section3.assetsCashDeposits ?? get('totalAssets', 'total_assets'), baseConfidence),
      liquid_assets: wrapField(financialInfo.liquidAssets ?? financialInfo.liquid_assets ?? section3.assetsCashDeposits, baseConfidence),
      annual_income: wrapField(financialInfo.annualIncome ?? financialInfo.annual_income ?? section3.incomeEmploymentSalary ?? get('annualIncome', 'annual_income'), baseConfidence),
      total_liabilities: wrapField(financialInfo.totalLiabilities ?? financialInfo.total_liabilities ?? section3.liabilitiesMortgages, baseConfidence),
      net_worth: wrapField(financialInfo.netWorth ?? financialInfo.net_worth ?? wealthSummary.netWorth ?? wealthSummary.net_worth, baseConfidence),
      net_worth_currency: wrapField(financialInfo.netWorthCurrency ?? financialInfo.net_worth_currency ?? wealthSummary.netWorthCurrency, baseConfidence),
      source_of_wealth: wrapField(financialInfo.sourceOfWealth ?? financialInfo.source_of_wealth ?? wealthSummary.sourceOfWealth ?? get('sourceOfWealth', 'source_of_wealth'), baseConfidence),
      source_of_funds: wrapField(financialInfo.sourceOfFunds ?? financialInfo.source_of_funds ?? wealthSummary.sourceOfFunds, baseConfidence),
      wealth_narrative: wrapField(financialInfo.wealthNarrative ?? financialInfo.wealth_narrative ?? wealthSummary.wealthNarrative, baseConfidence),
      securities: wrapField(financialInfo.securities ?? section3.assetsSecuritiesInvestments, baseConfidence),
      real_estate: wrapField(financialInfo.realEstate ?? financialInfo.real_estate ?? section3.assetsRealEstateMarketValue, baseConfidence),
    },
    // TASK-261: Add identification section
    identification: {
      passport_number: wrapField(identification.passportNumber ?? identification.passport_number ?? personalInfo.passportNumber, baseConfidence),
      passport_expiry: wrapField(identification.passportExpiry ?? identification.passport_expiry, baseConfidence),
      passport_country: wrapField(identification.passportCountry ?? identification.passport_country, baseConfidence),
      id_card_number: wrapField(identification.idCardNumber ?? identification.id_card_number ?? personalInfo.nationalId, baseConfidence),
      ahv_number: wrapField(identification.ahvNumber ?? identification.ahv_number ?? personalInfo.ssn, baseConfidence),
    },
    // TASK-261: Add personal background section
    personal_background: {
      education_level: wrapField(personalBackground.educationLevel ?? personalBackground.education_level, baseConfidence),
      institutions_attended: wrapField(personalBackground.institutionsAttended ?? personalBackground.institutions_attended, baseConfidence),
      professional_certifications: wrapField(personalBackground.professionalCertifications ?? personalBackground.professional_certifications, baseConfidence),
      hobbies_interests: wrapField(personalBackground.hobbiesInterests ?? personalBackground.hobbies_interests, baseConfidence),
      family_composition: wrapField(personalBackground.familyComposition ?? personalBackground.family_composition, baseConfidence),
      dependents_count: wrapField(personalBackground.dependentsCount ?? personalBackground.dependents_count ?? section1.dependentsCount, baseConfidence),
      spouse_employment_status: wrapField(personalBackground.spouseEmploymentStatus ?? personalBackground.spouse_employment_status, baseConfidence),
    },
    // TASK-261: Add wealth summary section
    wealth_summary: {
      net_worth: wrapField(wealthSummary.netWorth ?? wealthSummary.net_worth ?? financialInfo.netWorth, baseConfidence),
      liquid_net_worth: wrapField(wealthSummary.liquidNetWorth ?? wealthSummary.liquid_net_worth ?? financialInfo.liquidAssets, baseConfidence),
      net_worth_currency: wrapField(wealthSummary.netWorthCurrency ?? wealthSummary.net_worth_currency, baseConfidence),
      source_of_wealth: wrapField(wealthSummary.sourceOfWealth ?? wealthSummary.source_of_wealth ?? financialInfo.sourceOfWealth, baseConfidence),
      source_of_funds: wrapField(wealthSummary.sourceOfFunds ?? wealthSummary.source_of_funds ?? financialInfo.sourceOfFunds, baseConfidence),
      primary_wealth_source: wrapField(wealthSummary.primaryWealthSource ?? wealthSummary.primary_wealth_source, baseConfidence),
      wealth_narrative: wrapField(wealthSummary.wealthNarrative ?? wealthSummary.wealth_narrative ?? financialInfo.wealthNarrative, baseConfidence),
    },
    wealth_sources: wealthSources
      ? transformArray(wealthSources as Record<string, unknown>[], transformWealthSource, baseConfidence) as ExtractedClientData['wealth_sources']
      : undefined,
    income_sources: incomeSources
      ? transformArray(incomeSources as Record<string, unknown>[], transformIncomeSource, baseConfidence) as ExtractedClientData['income_sources']
      : undefined,
    business_activities: businessActivities
      ? transformArray(businessActivities as Record<string, unknown>[], transformBusinessActivity, baseConfidence) as ExtractedClientData['business_activities']
      : undefined,
  };
}
